<template>
  <div id="app" class="app-wrapper" :data-theme="activeTheme">
    <el-container style="min-height:100vh">
      <el-aside :width="sidebarCollapsed ? '64px' : '220px'" v-if="$route.path !== '/login'" class="sidebar" :class="{ collapsed: sidebarCollapsed }">
        <!-- 品牌区 -->
        <div class="brand">
          <img class="brand-icon" src="./assets/logo.png" alt="暖蜜" />
          <div class="brand-text">
            <h2 class="brand-title">暖蜜</h2>
            <p class="brand-sub">管理后台</p>
          </div>
        </div>
        <!-- 身份徽章卡：进后台第一眼确认「我是谁、能管什么」 -->
        <div class="role-card" :class="'role-' + roleKey">
          <div class="role-badge">
            <i :class="roleIcon"></i>
            <span>{{ roleLabel }}</span>
          </div>
          <p class="role-scope">{{ roleScope }}</p>
        </div>
        <!-- 导航菜单（声明式权限清单见 config.js MENU）：按业务域分组 + 每项带功能说明 -->
        <el-menu
          :default-active="$route.path"
          router
          background-color="transparent"
          text-color="var(--text-dim)"
          :active-text-color="menuActiveColor"
          class="nav-menu"
          :collapse="sidebarCollapsed"
          :collapse-transition="false"
        >
          <template v-for="g in menuGroups">
            <li :key="'group-' + g.name" class="menu-group-title" @click="toggleGroup(g.name)">
              <span>{{ g.name }}</span>
              <i class="el-icon-arrow-down group-arrow" :class="{ folded: collapsedGroups[g.name] }"></i>
            </li>
            <template v-if="!collapsedGroups[g.name]">
              <template v-for="m in g.items">
              <el-submenu v-if="m.children" :key="m.title" :index="m.title">
                <template slot="title">
                  <div class="nav-item">
                    <i :class="m.icon"></i>
                    <div class="nav-txt">
                      <span class="nav-t">{{ m.title }}</span>
                      <span class="nav-d">{{ m.desc }}</span>
                    </div>
                  </div>
                </template>
                <el-menu-item v-for="c in m.children" :key="c.path" :index="c.path">
                  <div class="nav-item nav-child">
                    <span class="nav-dot"></span>
                    <div class="nav-txt"><span class="nav-t">{{ c.title }}</span></div>
                  </div>
                </el-menu-item>
              </el-submenu>
              <el-menu-item v-else :key="m.path" :index="m.path">
                <div class="nav-item">
                  <i :class="m.icon"></i>
                  <div class="nav-txt">
                    <span class="nav-t">{{ m.title }}</span>
                    <span class="nav-d">{{ m.desc }}</span>
                  </div>
                </div>
              </el-menu-item>
            </template>
            </template>
          </template>
        </el-menu>
        <!-- 底部装饰线 -->
        <div class="sidebar-footer">
          <div class="dot-row"><span class="dot on"></span><span class="dot off"></span><span class="dot off"></span></div>
        </div>
      </el-aside>

      <el-container class="main-container">
        <!-- 顶栏 -->
        <el-header v-if="$route.path !== '/login'" class="header-bar">
          <div style="display:flex;align-items:center;min-width:0">
            <span class="collapse-toggle" @click="toggleSidebar" :title="sidebarCollapsed ? '展开菜单' : '收起菜单'">
              <i :class="sidebarCollapsed ? 'el-icon-s-unfold' : 'el-icon-s-fold'"></i>
            </span>
            <span class="header-greeting">
              欢迎回来 👋
              <span v-if="orgInfo" style="margin-left:8px;color:var(--brand-text);font-size:12px;background:rgba(244,114,182,0.1);padding:2px 10px;border-radius:10px">{{ orgInfo.name }}</span>
              <span v-if="guildInfo" style="margin-left:8px;color:var(--brand-text);font-size:12px;background:rgba(251,191,36,0.12);padding:2px 10px;border-radius:10px">公会：{{ guildInfo.name }}</span>
            </span>
          </div>
          <div style="display:flex;align-items:center;gap:16px">
            <!-- 主题切换 -->
            <el-popover placement="bottom" width="320" trigger="click" popper-class="theme-popover">
              <div class="theme-picker">
                <div class="theme-picker-header">
                  <span class="theme-picker-title">🎨 主题配色</span>
                  <span class="theme-picker-sub">一键切换管理后台视觉风格</span>
                </div>
                <div class="theme-grid">
                  <div
                    v-for="t in themes"
                    :key="t.key"
                    class="theme-card"
                    :class="{ active: activeTheme === t.key }"
                    @click="switchTheme(t.key)"
                  >
                    <span class="theme-card-preview" :style="t.previewStyle"></span>
                    <div class="theme-card-info">
                      <span class="theme-card-name" :style="{ color: t.key === activeTheme ? 'var(--brand-text)' : '' }">{{ t.name }}</span>
                      <span class="theme-card-badge" v-if="activeTheme === t.key">✓ 使用中</span>
                    </div>
                  </div>
                </div>
              </div>
              <span slot="reference" class="theme-btn" :style="themeBtnStyle">
                <span class="theme-btn-dot" :style="{ background: currentTheme.dotColor }"></span>
                <span class="theme-btn-text">{{ currentTheme.name }}</span>
                <i class="el-icon-arrow-down el-icon--right"></i>
              </span>
            </el-popover>
            <el-dropdown @command="handleCommand">
              <span class="header-user">
                <el-avatar size="small" icon="el-icon-user-solid" class="header-avatar"></el-avatar>
                <span>管理员</span>
                <i class="el-icon-arrow-down"></i>
              </span>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="logout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </el-header>

        <!-- 内容区 -->
        <el-main class="main-area">
          <router-view />
        </el-main>
      </el-container>
    </el-container>
  </div>
</template>
<script>
import { getMyOrg } from './api'
import { connect as wsConnect, disconnect as wsDisconnect } from './utils/socket'
import { MENU } from './config'
const THEME_STORAGE_KEY = 'admin_theme'

export default {
  provide() {
    return {
      orgPromise: new Promise(resolve => {
        this._resolveOrg = resolve
      }),
    }
  },
  data() {
    return {
      activeTheme: 'pink',
      // 侧边栏折叠状态（持久化，折叠时仅显示图标）
      sidebarCollapsed: localStorage.getItem('admin_sidebar_collapsed') === 'true',
      // 分组折叠状态（持久化，点击分组标题收起/展开该组菜单）
      collapsedGroups: {},
      // 2026-10-08 审计修复：默认必须「非超管」，等 checkOrg 异步确认后再放开超管菜单。
      // 原默认视为超管（!== 'false'），机构管理员刷新瞬间/篡改 localStorage 可见全部超管功能。
      isSuperAdmin: localStorage.getItem('admin_is_super') === 'true',
      // 身份三态：super=超管 org=机构管理员 guild=公会创作者(会长)，由 checkOrg 异步确认
      isGuildAdmin: localStorage.getItem('admin_scope') === 'guild',
      orgInfo: null,
      guildInfo: null,
      _resolveOrg: null,
      themes: [
        { key: 'pink',   name: '蜜桃粉', dotColor: '#f472b6', previewStyle: 'background:linear-gradient(135deg,#f472b6,#a78bfa,#c084fc,#f472b6,#a78bfa)' },
        { key: 'blue',   name: '深海蓝', dotColor: '#38bdf8', previewStyle: 'background:linear-gradient(135deg,#38bdf8,#3b82f6,#60a5fa,#38bdf8,#3b82f6)' },
        { key: 'green',  name: '翡翠绿', dotColor: '#34d399', previewStyle: 'background:linear-gradient(135deg,#34d399,#10b981,#6ee7b7,#34d399,#10b981)' },
        { key: 'orange', name: '落日橙', dotColor: '#fb923c', previewStyle: 'background:linear-gradient(135deg,#fb923c,#f97316,#fdba74,#fb923c,#f97316)' },
        { key: 'gold',   name: '鎏金',   dotColor: '#fbbf24', previewStyle: 'background:linear-gradient(135deg,#fbbf24,#f59e0b,#fde68a,#fbbf24,#f59e0b)' },
      ],
    }
  },
  computed: {
    /**
     * 声明式菜单权限清单（2026-10-08 定稿）：
     *   superOnly: true → 仅超级管理员（role=9）可见；
     *   其余菜单 机构管理员（role=1 / organization_admins）与超管共用，
     *   数据范围由后端按 org_code 自动过滤。
     * 新增页面时在这里声明一次即可，无需再写散落的 v-if。
     */
    visibleMenus() {
      // 身份三态 → 菜单可见性（清单见 config.js MENU）
      if (this.isSuperAdmin) return MENU                     // 超管：全部
      if (this.isGuildAdmin) return MENU.filter(m => m.guildVisible)  // 公会会长：仅公会管理
      return MENU.filter(m => !m.superOnly)                  // 机构管理员：除超管专属外
    },
    // 菜单按业务域分组（保持 MENU 声明顺序）
    menuGroups() {
      const groups = []
      const map = {}
      this.visibleMenus.forEach(m => {
        const g = m.group || '其它'
        if (!map[g]) { map[g] = { name: g, items: [] }; groups.push(map[g]) }
        map[g].items.push(m)
      })
      return groups
    },
    // 身份徽章：超管=金色 / 机构管理员=粉色 / 公会会长=琥珀色
    roleKey() { return this.isSuperAdmin ? 'super' : (this.isGuildAdmin ? 'guild' : 'org') },
    roleLabel() { return { super: '超级管理员', guild: '公会创作者', org: '机构管理员' }[this.roleKey] },
    roleIcon() { return { super: 'el-icon-s-claim', guild: 'el-icon-s-flag', org: 'el-icon-s-shop' }[this.roleKey] },
    roleScope() {
      if (this.isSuperAdmin) return '全平台数据 · 全部权限'
      if (this.isGuildAdmin) return this.guildInfo ? `管理公会：${this.guildInfo.name}` : '管理自己创建的公会'
      return this.orgInfo ? `数据范围：${this.orgInfo.name}` : '管理本机构数据'
    },
    currentTheme() {
      return this.themes.find(t => t.key === this.activeTheme) || this.themes[0]
    },
    themeBtnStyle() {
      const t = this.currentTheme
      const rgb = [parseInt(t.dotColor.slice(1,3),16), parseInt(t.dotColor.slice(3,5),16), parseInt(t.dotColor.slice(5,7),16)]
      return {
        background: `linear-gradient(135deg, rgba(${rgb[0]},${rgb[1]},${rgb[2]},0.2), rgba(${rgb[0]},${rgb[1]},${rgb[2]},0.06))`,
        borderColor: `rgba(${rgb[0]},${rgb[1]},${rgb[2]},0.27)`,
        color: t.dotColor,
      }
    },
    menuActiveColor() {
      const el = document.getElementById('app')
      if (el) return getComputedStyle(el).getPropertyValue('--text-brand').trim() || '#f0c4ff'
      return '#f0c4ff'
    },
  },
  created() {
    const saved = localStorage.getItem(THEME_STORAGE_KEY)
    if (saved && this.themes.some(t => t.key === saved)) this.activeTheme = saved
    // 恢复分组折叠记忆
    try {
      const map = {}
      ;(JSON.parse(localStorage.getItem('admin_menu_groups_collapsed') || '[]') || []).forEach(k => { map[k] = true })
      this.collapsedGroups = map
    } catch (e) { this.collapsedGroups = {} }
    this.checkOrg()
    wsConnect()
  },
  destroyed() {
    wsDisconnect()
  },
  watch: {
    $route(to, from) {
      if (from.path === '/login' && to.path !== '/login') {
        this.checkOrg()
      }
    }
  },
  methods: {
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed
      localStorage.setItem('admin_sidebar_collapsed', String(this.sidebarCollapsed))
    },
    toggleGroup(name) {
      // 整栏折叠态下分组标题只是分隔线，不响应点击
      if (this.sidebarCollapsed) return
      const next = { ...this.collapsedGroups }
      if (next[name]) delete next[name]
      else next[name] = true
      this.collapsedGroups = next
      localStorage.setItem('admin_menu_groups_collapsed', JSON.stringify(Object.keys(next)))
    },
    async checkOrg() {
      try {
        const res = await getMyOrg()
        if (res.code === 0 && res.data) {
          this.isSuperAdmin = res.data.is_super
          localStorage.setItem('admin_is_super', res.data.is_super)
          this.orgInfo = res.data.org
          this.guildInfo = res.data.guild || null
          this.isGuildAdmin = !!res.data.guild
          // 身份落盘：路由守卫按 scope 拦截越权页面（super / org / guild）
          const scope = res.data.is_super ? 'super' : (res.data.guild ? 'guild' : (res.data.org ? 'org' : 'none'))
          localStorage.setItem('admin_scope', scope)
          if (res.data.org && res.data.org.code) {
            localStorage.setItem('admin_org_code', res.data.org.code)
          } else {
            localStorage.removeItem('admin_org_code')
          }
        }
      } catch {} finally {
        if (this._resolveOrg) this._resolveOrg(true)
      }
    },
    handleCommand(cmd) {
      if (cmd === 'logout') {
        wsDisconnect()
        localStorage.removeItem('admin_token')
        localStorage.removeItem('admin_org_code')
        localStorage.removeItem('admin_is_super')
        localStorage.removeItem('admin_scope')
        this.$router.push('/login')
      }
    },
    switchTheme(key) {
      this.activeTheme = key
      localStorage.setItem(THEME_STORAGE_KEY, key)
    },
  },
}
</script>
<style>
/* ==========================================
   CSS 变量主题系统 —— 5套配色
   ========================================== */

/* ----- 默认 / 蜜桃粉 ----- */
#app {
  --bg-deep: #0a0a14;
  --bg-side-from: #15102a; --bg-side-to: #0e0a18;
  --bg-side-border: rgba(244,114,182,0.08);
  --bg-side-shadow: rgba(0,0,0,0.3);
  --brand-text: #f0c4ff;
  --brand-glow: 0 0 12px rgba(167,139,250,0.4);
  --brand-icon-glow: drop-shadow(0 0 8px rgba(244,114,182,0.5));
  --brand-sub: rgba(244,114,182,0.5);
  --brand-line: rgba(167,139,250,0.3);
  --menu-hover-bg: linear-gradient(90deg, rgba(244,114,182,0.12), rgba(167,139,250,0.06));
  --menu-active-bg: linear-gradient(90deg, rgba(236,72,153,0.25), rgba(167,139,250,0.12));
  --menu-active-bar: #f472b6;
  --menu-inline-bg: rgba(0,0,0,0.15);
  --header-from: rgba(21,16,42,0.95); --header-to: rgba(16,12,30,0.92);
  --header-border: rgba(244,114,182,0.08);
  --header-user-bg: rgba(255,255,255,0.04);
  --header-user-hover: rgba(244,114,182,0.1);
  --header-avatar-border: rgba(244,114,182,0.3);
  --main-radial1: radial-gradient(ellipse 80% 60% at 50% 0%, rgba(124,58,237,0.06) 0%, transparent 50%);
  --main-radial2: radial-gradient(ellipse 40% 50% at 100% 80%, rgba(244,114,182,0.04) 0%, transparent 50%);
  --main-base: #0d0d1a;
  --card-from: #1c1c30; --card-to: #18182a;
  --card-border: rgba(244,114,182,0.08);
  --card-hover-border: rgba(244,114,182,0.18);
  --card-hover-shadow: 0 4px 24px rgba(244,114,182,0.06);
  --card-header-bg: rgba(167,139,250,0.02);
  --table-bg: #1c1c30;
  --table-text: #e5e7eb;
  --table-th-from: #12122a; --table-th-to: #0f0f22;
  --table-th-color: #c4b5fd;
  --table-th-border: rgba(167,139,250,0.18);
  --table-td-color: #d1d5db;
  --table-td-border: rgba(255,255,255,0.04);
  --table-hover: rgba(244,114,182,0.06);
  --table-striped: rgba(255,255,255,0.02);
  --input-bg: #12122a;
  --input-border: rgba(255,255,255,0.08);
  --input-hover-border: rgba(244,114,182,0.3);
  --input-focus-border: #a78bfa;
  --input-focus-shadow: 0 0 0 3px rgba(167,139,250,0.1);
  --btn-primary-from: #a78bfa; --btn-primary-to: #8b5cf6;
  --btn-primary-hover-from: #b794fc; --btn-primary-hover-to: #9b6dfd;
  --btn-primary-shadow: rgba(167,139,250,0.3);
  --dialog-bg-from: #1c1c30; --dialog-bg-to: #18182a;
  --dialog-border: rgba(244,114,182,0.1);
  --dialog-title: #f0c4ff;
  --dropdown-bg: #1c1c30;
  --dropdown-border: rgba(244,114,182,0.12);
  --dropdown-hover: rgba(244,114,182,0.1);
  --dropdown-hover-text: #f0c4ff;
  --page-title: #f0c4ff;
  --tabs-active: #f472b6;
  --tabs-bar: linear-gradient(90deg, #f472b6, #a78bfa);
  --tag-info-bg: rgba(255,255,255,0.08);
  --tag-success-bg: rgba(52,211,153,0.12);
  --tag-warning-bg: rgba(251,191,36,0.12);
  --tag-danger-bg: rgba(248,113,113,0.12);
  --tag-primary-bg: rgba(167,139,250,0.15);
  --tag-info-color: #94a3b8;
  --tag-success-color: #34d399;
  --tag-warning-color: #fbbf24;
  --tag-danger-color: #f87171;
  --tag-primary-color: #a78bfa;
  --pager-bg: #12122a; --pager-color: #94a3b8;
  --pager-hover-color: #f472b6;
  --pager-active-from: #a78bfa; --pager-active-to: #8b5cf6;
  --radio-bg: #12122a; --radio-color: #94a3b8;
  --radio-active-bg: linear-gradient(135deg, rgba(244,114,182,0.2), rgba(167,139,250,0.15));
  --radio-active-border: rgba(244,114,182,0.3);
  --text-main: #e5e7eb;
  --text-dim: rgba(255,255,255,0.55);
  --text-muted: rgba(255,255,255,0.45);
  --text-link: #94a3b8;
  --text-link-hover: #f0c4ff;
  --close-hover: #f472b6;
  --scrollbar-thumb: rgba(244,114,182,0.2);
  --scrollbar-thumb-hover: rgba(244,114,182,0.35);
  --label-color: rgba(255,255,255,0.55);
  --dot-on: #f472b6;
  --dot-on-glow: 0 0 6px #f472b6;
  --dot-off: rgba(255,255,255,0.1);
}

/* ----- 深海蓝 ----- */
#app[data-theme="blue"] {
  --bg-deep: #070e18;
  --bg-side-from: #0d1728; --bg-side-to: #070e1a;
  --bg-side-border: rgba(56,189,248,0.08);
  --bg-side-shadow: rgba(0,0,0,0.3);
  --brand-text: #bae6fd;
  --brand-glow: 0 0 12px rgba(59,130,246,0.4);
  --brand-icon-glow: drop-shadow(0 0 8px rgba(56,189,248,0.5));
  --brand-sub: rgba(56,189,248,0.5);
  --brand-line: rgba(59,130,246,0.3);
  --menu-hover-bg: linear-gradient(90deg, rgba(56,189,248,0.12), rgba(59,130,246,0.06));
  --menu-active-bg: linear-gradient(90deg, rgba(14,165,233,0.25), rgba(59,130,246,0.12));
  --menu-active-bar: #38bdf8;
  --menu-inline-bg: rgba(0,0,0,0.15);
  --header-from: rgba(13,23,40,0.95); --header-to: rgba(7,14,26,0.92);
  --header-border: rgba(56,189,248,0.08);
  --header-user-bg: rgba(255,255,255,0.04);
  --header-user-hover: rgba(56,189,248,0.1);
  --header-avatar-border: rgba(56,189,248,0.3);
  --main-radial1: radial-gradient(ellipse 80% 60% at 50% 0%, rgba(14,165,233,0.06) 0%, transparent 50%);
  --main-radial2: radial-gradient(ellipse 40% 50% at 100% 80%, rgba(56,189,248,0.04) 0%, transparent 50%);
  --main-base: #070e18;
  --card-from: #14223a; --card-to: #0f1c2e;
  --card-border: rgba(56,189,248,0.08);
  --card-hover-border: rgba(56,189,248,0.18);
  --card-hover-shadow: 0 4px 24px rgba(56,189,248,0.06);
  --card-header-bg: rgba(59,130,246,0.02);
  --table-bg: #14223a;
  --table-text: #e5e7eb;
  --table-th-from: #0e1828; --table-th-to: #0a1422;
  --table-th-color: #93c5fd;
  --table-th-border: rgba(59,130,246,0.18);
  --table-td-color: #d1d5db;
  --table-td-border: rgba(255,255,255,0.04);
  --table-hover: rgba(56,189,248,0.06);
  --table-striped: rgba(255,255,255,0.02);
  --input-bg: #0e1828;
  --input-border: rgba(255,255,255,0.08);
  --input-hover-border: rgba(56,189,248,0.3);
  --input-focus-border: #3b82f6;
  --input-focus-shadow: 0 0 0 3px rgba(59,130,246,0.1);
  --btn-primary-from: #3b82f6; --btn-primary-to: #2563eb;
  --btn-primary-hover-from: #60a5fa; --btn-primary-hover-to: #3b82f6;
  --btn-primary-shadow: rgba(59,130,246,0.3);
  --dialog-bg-from: #14223a; --dialog-bg-to: #0f1c2e;
  --dialog-border: rgba(56,189,248,0.1);
  --dialog-title: #bae6fd;
  --dropdown-bg: #14223a;
  --dropdown-border: rgba(56,189,248,0.12);
  --dropdown-hover: rgba(56,189,248,0.1);
  --dropdown-hover-text: #bae6fd;
  --page-title: #bae6fd;
  --tabs-active: #38bdf8;
  --tabs-bar: linear-gradient(90deg, #38bdf8, #3b82f6);
  --tag-info-bg: rgba(255,255,255,0.08);
  --tag-success-bg: rgba(52,211,153,0.12);
  --tag-warning-bg: rgba(251,191,36,0.12);
  --tag-danger-bg: rgba(248,113,113,0.12);
  --tag-primary-bg: rgba(59,130,246,0.15);
  --tag-info-color: #94a3b8;
  --tag-success-color: #34d399;
  --tag-warning-color: #fbbf24;
  --tag-danger-color: #f87171;
  --tag-primary-color: #60a5fa;
  --pager-bg: #0e1828; --pager-color: #94a3b8;
  --pager-hover-color: #38bdf8;
  --pager-active-from: #3b82f6; --pager-active-to: #2563eb;
  --radio-bg: #0e1828; --radio-color: #94a3b8;
  --radio-active-bg: linear-gradient(135deg, rgba(56,189,248,0.2), rgba(59,130,246,0.15));
  --radio-active-border: rgba(56,189,248,0.3);
  --text-main: #e5e7eb;
  --text-dim: rgba(255,255,255,0.55);
  --text-muted: rgba(255,255,255,0.45);
  --text-link: #94a3b8;
  --text-link-hover: #bae6fd;
  --close-hover: #38bdf8;
  --scrollbar-thumb: rgba(56,189,248,0.2);
  --scrollbar-thumb-hover: rgba(56,189,248,0.35);
  --label-color: rgba(255,255,255,0.55);
  --dot-on: #38bdf8;
  --dot-on-glow: 0 0 6px #38bdf8;
  --dot-off: rgba(255,255,255,0.1);
}

/* ----- 翡翠绿 ----- */
#app[data-theme="green"] {
  --bg-deep: #07120c;
  --bg-side-from: #0e1a12; --bg-side-to: #08140e;
  --bg-side-border: rgba(52,211,153,0.08);
  --bg-side-shadow: rgba(0,0,0,0.3);
  --brand-text: #a7f3d0;
  --brand-glow: 0 0 12px rgba(16,185,129,0.4);
  --brand-icon-glow: drop-shadow(0 0 8px rgba(52,211,153,0.5));
  --brand-sub: rgba(52,211,153,0.5);
  --brand-line: rgba(16,185,129,0.3);
  --menu-hover-bg: linear-gradient(90deg, rgba(52,211,153,0.12), rgba(16,185,129,0.06));
  --menu-active-bg: linear-gradient(90deg, rgba(5,150,105,0.25), rgba(16,185,129,0.12));
  --menu-active-bar: #34d399;
  --header-from: rgba(14,26,18,0.95); --header-to: rgba(8,20,14,0.92);
  --header-border: rgba(52,211,153,0.08);
  --header-user-bg: rgba(255,255,255,0.04);
  --header-user-hover: rgba(52,211,153,0.1);
  --header-avatar-border: rgba(52,211,153,0.3);
  --main-radial1: radial-gradient(ellipse 80% 60% at 50% 0%, rgba(5,150,105,0.06) 0%, transparent 50%);
  --main-radial2: radial-gradient(ellipse 40% 50% at 100% 80%, rgba(52,211,153,0.04) 0%, transparent 50%);
  --main-base: #07120c;
  --card-from: #142e1f; --card-to: #102818;
  --card-border: rgba(52,211,153,0.08);
  --card-hover-border: rgba(52,211,153,0.18);
  --card-hover-shadow: 0 4px 24px rgba(52,211,153,0.06);
  --card-header-bg: rgba(16,185,129,0.02);
  --table-bg: #142e1f;
  --table-text: #e5e7eb;
  --table-th-from: #0e1f14; --table-th-to: #0a1a10;
  --table-th-color: #86efac;
  --table-th-border: rgba(16,185,129,0.18);
  --table-td-color: #d1d5db;
  --table-td-border: rgba(255,255,255,0.04);
  --table-hover: rgba(52,211,153,0.06);
  --table-striped: rgba(255,255,255,0.02);
  --input-bg: #0e1f14;
  --input-border: rgba(255,255,255,0.08);
  --input-hover-border: rgba(52,211,153,0.3);
  --input-focus-border: #10b981;
  --input-focus-shadow: 0 0 0 3px rgba(16,185,129,0.1);
  --btn-primary-from: #10b981; --btn-primary-to: #059669;
  --btn-primary-hover-from: #34d399; --btn-primary-hover-to: #10b981;
  --btn-primary-shadow: rgba(16,185,129,0.3);
  --dialog-bg-from: #142e1f; --dialog-bg-to: #102818;
  --dialog-border: rgba(52,211,153,0.1);
  --dialog-title: #a7f3d0;
  --dropdown-bg: #142e1f;
  --dropdown-border: rgba(52,211,153,0.12);
  --dropdown-hover: rgba(52,211,153,0.1);
  --dropdown-hover-text: #a7f3d0;
  --page-title: #a7f3d0;
  --tabs-active: #34d399;
  --tabs-bar: linear-gradient(90deg, #34d399, #10b981);
  --tag-primary-bg: rgba(16,185,129,0.15);
  --tag-primary-color: #34d399;
  --pager-bg: #0e1f14; --pager-color: #94a3b8;
  --pager-hover-color: #34d399;
  --pager-active-from: #10b981; --pager-active-to: #059669;
  --radio-bg: #0e1f14; --radio-color: #94a3b8;
  --radio-active-bg: linear-gradient(135deg, rgba(52,211,153,0.2), rgba(16,185,129,0.15));
  --radio-active-border: rgba(52,211,153,0.3);
  --text-main: #e5e7eb;
  --text-dim: rgba(255,255,255,0.55);
  --text-muted: rgba(255,255,255,0.45);
  --text-link: #94a3b8;
  --text-link-hover: #a7f3d0;
  --close-hover: #34d399;
  --scrollbar-thumb: rgba(52,211,153,0.2);
  --scrollbar-thumb-hover: rgba(52,211,153,0.35);
  --label-color: rgba(255,255,255,0.55);
  --dot-on: #34d399;
  --dot-on-glow: 0 0 6px #34d399;
  --dot-off: rgba(255,255,255,0.1);
}

/* ----- 落日橙 ----- */
#app[data-theme="orange"] {
  --bg-deep: #140c08;
  --bg-side-from: #1a120e; --bg-side-to: #140c08;
  --bg-side-border: rgba(251,146,60,0.08);
  --bg-side-shadow: rgba(0,0,0,0.3);
  --brand-text: #fed7aa;
  --brand-glow: 0 0 12px rgba(249,115,22,0.4);
  --brand-icon-glow: drop-shadow(0 0 8px rgba(251,146,60,0.5));
  --brand-sub: rgba(251,146,60,0.5);
  --brand-line: rgba(249,115,22,0.3);
  --menu-hover-bg: linear-gradient(90deg, rgba(251,146,60,0.12), rgba(249,115,22,0.06));
  --menu-active-bg: linear-gradient(90deg, rgba(234,88,12,0.25), rgba(249,115,22,0.12));
  --menu-active-bar: #fb923c;
  --header-from: rgba(26,18,14,0.95); --header-to: rgba(20,12,8,0.92);
  --header-border: rgba(251,146,60,0.08);
  --header-user-bg: rgba(255,255,255,0.04);
  --header-user-hover: rgba(251,146,60,0.1);
  --header-avatar-border: rgba(251,146,60,0.3);
  --main-radial1: radial-gradient(ellipse 80% 60% at 50% 0%, rgba(234,88,12,0.06) 0%, transparent 50%);
  --main-radial2: radial-gradient(ellipse 40% 50% at 100% 80%, rgba(251,146,60,0.04) 0%, transparent 50%);
  --main-base: #140c08;
  --card-from: #2e1e14; --card-to: #281810;
  --card-border: rgba(251,146,60,0.08);
  --card-hover-border: rgba(251,146,60,0.18);
  --card-hover-shadow: 0 4px 24px rgba(251,146,60,0.06);
  --card-header-bg: rgba(249,115,22,0.02);
  --table-bg: #2e1e14;
  --table-text: #e5e7eb;
  --table-th-from: #1f140c; --table-th-to: #1a1008;
  --table-th-color: #fdba74;
  --table-th-border: rgba(249,115,22,0.18);
  --table-td-color: #d1d5db;
  --table-td-border: rgba(255,255,255,0.04);
  --table-hover: rgba(251,146,60,0.06);
  --table-striped: rgba(255,255,255,0.02);
  --input-bg: #1f140c;
  --input-border: rgba(255,255,255,0.08);
  --input-hover-border: rgba(251,146,60,0.3);
  --input-focus-border: #f97316;
  --input-focus-shadow: 0 0 0 3px rgba(249,115,22,0.1);
  --btn-primary-from: #f97316; --btn-primary-to: #ea580c;
  --btn-primary-hover-from: #fb923c; --btn-primary-hover-to: #f97316;
  --btn-primary-shadow: rgba(249,115,22,0.3);
  --dialog-bg-from: #2e1e14; --dialog-bg-to: #281810;
  --dialog-border: rgba(251,146,60,0.1);
  --dialog-title: #fed7aa;
  --dropdown-bg: #2e1e14;
  --dropdown-border: rgba(251,146,60,0.12);
  --dropdown-hover: rgba(251,146,60,0.1);
  --dropdown-hover-text: #fed7aa;
  --page-title: #fed7aa;
  --tabs-active: #fb923c;
  --tabs-bar: linear-gradient(90deg, #fb923c, #f97316);
  --tag-primary-bg: rgba(249,115,22,0.15);
  --tag-primary-color: #fb923c;
  --pager-bg: #1f140c; --pager-color: #94a3b8;
  --pager-hover-color: #fb923c;
  --pager-active-from: #f97316; --pager-active-to: #ea580c;
  --radio-bg: #1f140c; --radio-color: #94a3b8;
  --radio-active-bg: linear-gradient(135deg, rgba(251,146,60,0.2), rgba(249,115,22,0.15));
  --radio-active-border: rgba(251,146,60,0.3);
  --text-main: #e5e7eb;
  --text-dim: rgba(255,255,255,0.55);
  --text-muted: rgba(255,255,255,0.45);
  --text-link: #94a3b8;
  --text-link-hover: #fed7aa;
  --close-hover: #fb923c;
  --scrollbar-thumb: rgba(251,146,60,0.2);
  --scrollbar-thumb-hover: rgba(251,146,60,0.35);
  --label-color: rgba(255,255,255,0.55);
  --dot-on: #fb923c;
  --dot-on-glow: 0 0 6px #fb923c;
  --dot-off: rgba(255,255,255,0.1);
}

/* ----- 鎏金 ----- */
#app[data-theme="gold"] {
  --bg-deep: #100e08;
  --bg-side-from: #1a160e; --bg-side-to: #100e08;
  --bg-side-border: rgba(251,191,36,0.08);
  --bg-side-shadow: rgba(0,0,0,0.3);
  --brand-text: #fde68a;
  --brand-glow: 0 0 12px rgba(245,158,11,0.4);
  --brand-icon-glow: drop-shadow(0 0 8px rgba(251,191,36,0.5));
  --brand-sub: rgba(251,191,36,0.5);
  --brand-line: rgba(245,158,11,0.3);
  --menu-hover-bg: linear-gradient(90deg, rgba(251,191,36,0.12), rgba(245,158,11,0.06));
  --menu-active-bg: linear-gradient(90deg, rgba(217,119,6,0.25), rgba(245,158,11,0.12));
  --menu-active-bar: #fbbf24;
  --header-from: rgba(26,22,14,0.95); --header-to: rgba(16,14,8,0.92);
  --header-border: rgba(251,191,36,0.08);
  --header-user-bg: rgba(255,255,255,0.04);
  --header-user-hover: rgba(251,191,36,0.1);
  --header-avatar-border: rgba(251,191,36,0.3);
  --main-radial1: radial-gradient(ellipse 80% 60% at 50% 0%, rgba(217,119,6,0.06) 0%, transparent 50%);
  --main-radial2: radial-gradient(ellipse 40% 50% at 100% 80%, rgba(251,191,36,0.04) 0%, transparent 50%);
  --main-base: #100e08;
  --card-from: #2e2614; --card-to: #282010;
  --card-border: rgba(251,191,36,0.08);
  --card-hover-border: rgba(251,191,36,0.18);
  --card-hover-shadow: 0 4px 24px rgba(251,191,36,0.06);
  --card-header-bg: rgba(245,158,11,0.02);
  --table-bg: #2e2614;
  --table-text: #e5e7eb;
  --table-th-from: #1f1a0c; --table-th-to: #1a1608;
  --table-th-color: #fcd34d;
  --table-th-border: rgba(245,158,11,0.18);
  --table-td-color: #d1d5db;
  --table-td-border: rgba(255,255,255,0.04);
  --table-hover: rgba(251,191,36,0.06);
  --table-striped: rgba(255,255,255,0.02);
  --input-bg: #1f1a0c;
  --input-border: rgba(255,255,255,0.08);
  --input-hover-border: rgba(251,191,36,0.3);
  --input-focus-border: #f59e0b;
  --input-focus-shadow: 0 0 0 3px rgba(245,158,11,0.1);
  --btn-primary-from: #f59e0b; --btn-primary-to: #d97706;
  --btn-primary-hover-from: #fbbf24; --btn-primary-hover-to: #f59e0b;
  --btn-primary-shadow: rgba(245,158,11,0.3);
  --dialog-bg-from: #2e2614; --dialog-bg-to: #282010;
  --dialog-border: rgba(251,191,36,0.1);
  --dialog-title: #fde68a;
  --dropdown-bg: #2e2614;
  --dropdown-border: rgba(251,191,36,0.12);
  --dropdown-hover: rgba(251,191,36,0.1);
  --dropdown-hover-text: #fde68a;
  --page-title: #fde68a;
  --tabs-active: #fbbf24;
  --tabs-bar: linear-gradient(90deg, #fbbf24, #f59e0b);
  --tag-primary-bg: rgba(245,158,11,0.15);
  --tag-primary-color: #fbbf24;
  --pager-bg: #1f1a0c; --pager-color: #94a3b8;
  --pager-hover-color: #fbbf24;
  --pager-active-from: #f59e0b; --pager-active-to: #d97706;
  --radio-bg: #1f1a0c; --radio-color: #94a3b8;
  --radio-active-bg: linear-gradient(135deg, rgba(251,191,36,0.2), rgba(245,158,11,0.15));
  --radio-active-border: rgba(251,191,36,0.3);
  --text-main: #e5e7eb;
  --text-dim: rgba(255,255,255,0.55);
  --text-muted: rgba(255,255,255,0.45);
  --text-link: #94a3b8;
  --text-link-hover: #fde68a;
  --close-hover: #fbbf24;
  --scrollbar-thumb: rgba(251,191,36,0.2);
  --scrollbar-thumb-hover: rgba(251,191,36,0.35);
  --label-color: rgba(255,255,255,0.55);
  --dot-on: #fbbf24;
  --dot-on-glow: 0 0 6px #fbbf24;
  --dot-off: rgba(255,255,255,0.1);
}

/* ==========================================
   通用样式（引用 CSS 变量）
   ========================================== */

body {
  margin:0;
  font-family:-apple-system,BlinkMacSystemFont,Segoe UI,PingFang SC,Hiragino Sans GB,Microsoft YaHei,Helvetica Neue,Helvetica,Arial,sans-serif;
  background: var(--bg-deep);
}

.app-wrapper { background: var(--bg-deep); }

/* ===== 侧边栏 ===== */
.sidebar {
  background: linear-gradient(180deg, var(--bg-side-from) 0%, var(--bg-side-from) 40%, var(--bg-side-to) 100%) !important;
  overflow-y: auto;
  position: relative;
  border-right: 1px solid var(--bg-side-border);
  box-shadow: 2px 0 24px var(--bg-side-shadow);
}

/* 品牌区 */
.brand {
  padding: 28px 20px 22px;
  text-align: center;
  border-bottom: 1px solid var(--bg-side-border);
  position: relative;
}
.brand::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 20%;
  width: 60%;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--brand-line), transparent);
}
.brand-icon { width: 44px; height: 44px; border-radius: 12px; margin-bottom: 8px; object-fit: cover; filter: var(--brand-icon-glow); }
.brand-title { color: var(--brand-text); margin: 0; font-size: 18px; letter-spacing: 2px; font-weight: 700; text-shadow: var(--brand-glow); }
.brand-sub { color: var(--brand-sub); font-size: 11px; margin: 4px 0 0; letter-spacing: 4px; text-transform: uppercase; }

/* ===== 身份徽章卡：三种角色三种色（超管金 / 机构粉 / 会长琥珀） ===== */
.role-card {
  margin: 12px 14px 2px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--bg-side-border);
}
.role-badge { display: flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 700; letter-spacing: 1px; }
.role-badge i { font-size: 15px; }
.role-super .role-badge { color: #fbbf24; }
.role-super .role-card { border-color: rgba(251, 191, 36, 0.25); background: rgba(251, 191, 36, 0.06); }
.role-org .role-badge { color: #f472b6; }
.role-org .role-card { border-color: rgba(244, 114, 182, 0.25); background: rgba(244, 114, 182, 0.06); }
.role-guild .role-badge { color: #f59e0b; }
.role-guild .role-card { border-color: rgba(245, 158, 11, 0.25); background: rgba(245, 158, 11, 0.06); }
.role-scope { margin: 5px 0 0; font-size: 11px; color: var(--text-dim); line-height: 1.4;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* ===== 分组标题（点击收起/展开该组） ===== */
.menu-group-title {
  padding: 14px 22px 4px;
  font-size: 11px;
  color: var(--text-dim);
  opacity: 0.65;
  letter-spacing: 2px;
  list-style: none;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
}
.menu-group-title:hover { opacity: 1; color: var(--brand-text); }
.group-arrow { font-size: 12px; transition: transform 0.2s ease; }
.group-arrow.folded { transform: rotate(-90deg); }
/* 首个分组标题贴近身份卡 */
.menu-group-title:first-of-type { padding-top: 8px; }

/* ===== 双行菜单项：标题 + 一句话功能说明 ===== */
.nav-menu { border-right: none !important; margin-top: 4px; }
.nav-menu .el-menu-item,
.nav-menu .el-submenu__title {
  height: auto;
  min-height: 50px;
  line-height: 1.3;
  padding-top: 7px;
  padding-bottom: 7px;
  margin: 2px 10px;
  border-radius: 8px;
  transition: all 0.25s ease;
  font-size: 14px;
  display: flex;
  align-items: center;
}
.nav-item { display: flex; align-items: center; min-width: 0; }
.nav-item > i { margin-right: 8px; font-size: 16px; flex-shrink: 0; }
.nav-txt { display: flex; flex-direction: column; line-height: 1.25; min-width: 0; }
.nav-t { font-size: 13.5px; }
.nav-d { font-size: 10.5px; color: var(--text-dim); opacity: 0.8; margin-top: 2px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 140px; }
.nav-child .nav-dot { width: 5px; height: 5px; border-radius: 50%; background: currentColor;
  opacity: 0.5; margin: 0 9px 0 4px; flex-shrink: 0; }
.nav-menu .el-menu-item i,
.nav-menu .el-submenu__title i { margin-right: 0; font-size: 16px; }
.nav-menu .el-menu-item:hover,
.nav-menu .el-submenu__title:hover {
  background: var(--menu-hover-bg) !important;
  color: var(--brand-text) !important;
}
.nav-menu .el-menu-item:hover .nav-d { color: var(--brand-text); opacity: 0.75; }
.nav-menu .el-menu-item.is-active {
  background: var(--menu-active-bg) !important;
  color: var(--brand-text) !important;
  font-weight: 600;
  box-shadow: inset 3px 0 0 var(--menu-active-bar);
}
.nav-menu .el-menu-item.is-active .nav-d { color: var(--brand-text); opacity: 0.8; }
.nav-menu .el-menu--inline {
  background: var(--menu-inline-bg) !important;
  margin: 0 10px;
  border-radius: 6px;
}
.nav-menu .el-menu--inline .el-menu-item {
  min-height: 42px;
  padding-left: 44px !important;
  font-size: 13px;
}

/* 侧边栏底部装饰 */
.sidebar-footer {
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  text-align: center;
}
.dot-row { display: flex; gap: 6px; justify-content: center; }
.dot { width: 6px; height: 6px; border-radius: 50%; }
.dot.on { background: var(--dot-on); box-shadow: var(--dot-on-glow); }
.dot.off { background: var(--dot-off); }

/* ===== 侧边栏折叠态（顶栏按钮切换，状态持久化） ===== */
.collapse-toggle {
  font-size: 20px;
  color: var(--text-dim);
  cursor: pointer;
  margin-right: 14px;
  line-height: 1;
  flex-shrink: 0;
}
.collapse-toggle:hover { color: var(--brand-text); }

/* 折叠：只留图标，文字/说明/身份卡细节全部隐藏 */
.sidebar.collapsed .brand { padding: 20px 8px 14px; }
.sidebar.collapsed .brand-icon { width: 36px; height: 36px; margin-bottom: 0; }
.sidebar.collapsed .brand-text { display: none; }
.sidebar.collapsed .role-card { margin: 10px 8px 2px; padding: 8px 4px; }
.sidebar.collapsed .role-badge { justify-content: center; }
.sidebar.collapsed .role-badge span,
.sidebar.collapsed .role-scope { display: none; }
.sidebar.collapsed .menu-group-title {
  padding: 10px 8px 2px;
  font-size: 0;
  letter-spacing: 0;
  cursor: default;
}
.sidebar.collapsed .group-arrow { display: none; }
.sidebar.collapsed .menu-group-title::after {
  content: '';
  display: block;
  height: 1px;
  margin: 2px 8px 0;
  background: linear-gradient(90deg, transparent, var(--bg-side-border), transparent);
}
.sidebar.collapsed .nav-menu .el-menu-item,
.sidebar.collapsed .nav-menu .el-submenu__title {
  margin: 2px 8px;
  padding-left: 0 !important;
  padding-right: 0 !important;
  justify-content: center;
}
.sidebar.collapsed .nav-item { justify-content: center; }
.sidebar.collapsed .nav-item > i { margin-right: 0; }
.sidebar.collapsed .nav-txt { display: none; }
.sidebar.collapsed .nav-menu .el-menu--inline { display: none; }

/* ===== 主区域 ===== */
.main-container { position: relative; }

/* 顶栏 */
.header-bar {
  background: linear-gradient(180deg, var(--header-from), var(--header-to)) !important;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--header-border) !important;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  height: 56px;
}
.header-greeting {
  color: var(--text-muted);
  font-size: 13px;
  letter-spacing: 1px;
}

/* 主题切换按钮 — 带色标醒目样式 */
.theme-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px 6px 10px;
  border-radius: 20px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid transparent;
  transition: all 0.3s ease;
  user-select: none;
}
.theme-btn:hover { filter: brightness(1.25); transform: scale(1.03); }
.theme-btn-dot {
  width: 10px; height: 10px;
  border-radius: 50%;
  box-shadow: 0 0 6px currentColor;
  flex-shrink: 0;
}
.theme-btn-text { white-space: nowrap; }
.theme-btn i { font-size: 11px; opacity: 0.7; }

/* 主题选择面板 -- popover */
.theme-popover { padding: 0 !important; }
.theme-picker { padding: 8px 0; }
.theme-picker-header { padding: 8px 16px 12px; border-bottom: 1px solid var(--card-border); margin-bottom: 8px; }
.theme-picker-title { display:block; color: var(--text-main); font-size: 14px; font-weight: 600; margin-bottom: 4px; }
.theme-picker-sub { display:block; color: var(--text-muted); font-size: 11px; }
.theme-grid { display: flex; flex-direction: column; gap: 2px; padding: 0 8px; }

/* 主题卡片 */
.theme-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}
.theme-card:hover { background: var(--header-user-hover); }
.theme-card.active {
  background: var(--menu-active-bg) !important;
  border-color: var(--card-hover-border);
}
.theme-card-preview {
  width: 36px; height: 36px;
  border-radius: 8px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}
.theme-card-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.theme-card-name { font-size: 13px; color: var(--text-main); font-weight: 500; }
.theme-card-badge { font-size: 11px; color: var(--brand-text); opacity: 0.8; }
.theme-card.active .theme-card-name { color: var(--brand-text); font-weight: 600; }

/* 用户下拉 */
.header-user {
  color: var(--text-main);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 14px 6px 8px;
  border-radius: 20px;
  background: var(--header-user-bg);
  transition: all 0.25s;
  font-size: 14px;
}
.header-user:hover { background: var(--header-user-hover); color: var(--brand-text); }
.header-user i { color: var(--text-dim); font-size: 12px; }
.header-avatar { border: 2px solid var(--header-avatar-border); }

/* 内容区 */
.main-area {
  background: var(--main-radial1), var(--main-radial2), var(--main-base) !important;
  padding: 24px;
  min-height: calc(100vh - 56px);
}

/* ==========================================
   Element UI 全局覆盖
   ========================================== */

/* ----- 卡片 ----- */
.el-card {
  background: linear-gradient(135deg, var(--card-from) 0%, var(--card-to) 100%) !important;
  border: 1px solid var(--card-border) !important;
  border-radius: 12px !important;
  overflow: hidden;
  transition: all 0.3s ease;
}
.el-card:hover {
  border-color: var(--card-hover-border) !important;
  box-shadow: var(--card-hover-shadow);
}
.el-card__body { color: var(--table-td-color); }
.el-card__header {
  border-bottom: 1px solid var(--card-border) !important;
  padding: 14px 20px !important;
  background: var(--card-header-bg);
}

/* ----- 表格 ----- */
.el-table {
  background: var(--table-bg) !important;
  color: var(--table-text) !important;
  font-size: 14px;
  border-radius: 8px;
  overflow: hidden;
}
.el-table .el-table__body-wrapper,
.el-table .el-table__body,
.el-table .el-table__header-wrapper,
.el-table .el-table__header,
.el-table .el-table__footer-wrapper { background: transparent !important; }
.el-table th {
  background: linear-gradient(180deg, var(--table-th-from), var(--table-th-to)) !important;
  color: var(--table-th-color) !important;
  font-weight: 600;
  font-size: 13px;
  border-bottom: 2px solid var(--table-th-border) !important;
  padding: 13px 0;
  letter-spacing: 1px;
}
.el-table td {
  border-bottom: 1px solid var(--table-td-border) !important;
  color: var(--table-td-color) !important;
  padding: 11px 0;
}
.el-table__body tr { background: transparent !important; }
.el-table__body tr:hover > td { background: var(--table-hover) !important; }
.el-table--striped .el-table__body tr.el-table__row--striped td { background: var(--table-striped) !important; }
.el-table::before { background-color: transparent !important; }
.el-table__empty-block { background: var(--table-bg) !important; }
.el-table__empty-text { color: var(--text-muted); }

/* ----- 弹窗 ----- */
.el-dialog {
  background: linear-gradient(135deg, var(--dialog-bg-from) 0%, var(--dialog-bg-to) 100%) !important;
  border-radius: 14px !important;
  border: 1px solid var(--dialog-border) !important;
  box-shadow: 0 12px 48px rgba(0,0,0,0.5);
}
.el-dialog__header { padding: 20px 24px 14px; border-bottom: 1px solid var(--card-border); }
.el-dialog__title { color: var(--dialog-title) !important; font-weight: 700; font-size: 16px; }
.el-dialog__body { color: var(--table-td-color); padding: 20px 24px; }

/* ----- 表单 ----- */
.el-form-item__label { color: var(--label-color) !important; font-weight: 500; }
.el-pagination__total, .el-pagination__jump { color: var(--text-dim) !important; }
.el-input__inner {
  background: var(--input-bg) !important;
  border: 1px solid var(--input-border) !important;
  color: var(--text-main) !important;
  border-radius: 8px !important;
  transition: all 0.25s;
}
.el-input__inner:hover { border-color: var(--input-hover-border) !important; }
.el-input__inner:focus {
  border-color: var(--input-focus-border) !important;
  box-shadow: var(--input-focus-shadow);
}
.el-select .el-input__inner { background: var(--input-bg) !important; }
.el-textarea__inner {
  background: var(--input-bg) !important;
  border-color: var(--input-border) !important;
  color: var(--text-main) !important;
  border-radius: 8px !important;
}

/* ----- 标签 ----- */
.el-tag { border: none !important; border-radius: 6px !important; font-weight: 500; }
.el-tag--info { background: var(--tag-info-bg) !important; color: var(--tag-info-color) !important; }
.el-tag--success { background: var(--tag-success-bg) !important; color: var(--tag-success-color) !important; }
.el-tag--warning { background: var(--tag-warning-bg) !important; color: var(--tag-warning-color) !important; }
.el-tag--danger { background: var(--tag-danger-bg) !important; color: var(--tag-danger-color) !important; }
.el-tag--primary { background: var(--tag-primary-bg) !important; color: var(--tag-primary-color) !important; }

/* ----- 按钮 ----- */
.el-button--primary {
  background: linear-gradient(135deg, var(--btn-primary-from), var(--btn-primary-to)) !important;
  border: none !important;
  border-radius: 8px !important;
  font-weight: 500;
  transition: all 0.3s;
  color: #fff !important;
}
.el-button--primary:hover {
  background: linear-gradient(135deg, var(--btn-primary-hover-from), var(--btn-primary-hover-to)) !important;
  box-shadow: 0 4px 16px var(--btn-primary-shadow);
}
.el-button--danger {
  background: linear-gradient(135deg, #f87171, #ef4444) !important;
  border: none !important;
  border-radius: 8px !important;
  color: #fff !important;
}
.el-button--success {
  background: linear-gradient(135deg, #34d399, #10b981) !important;
  border: none !important;
  border-radius: 8px !important;
  color: #fff !important;
}
.el-button--warning {
  background: linear-gradient(135deg, #fbbf24, #f59e0b) !important;
  border: none !important;
  border-radius: 8px !important;
  color: #1a1a2e !important;
}

/* ----- 表格操作按钮（text 类型）----- */
.el-button--text,
.el-button--text:focus,
.el-button--text:active {
  background: transparent !important;
  border: none !important;
  color: var(--text-link) !important;
  padding: 5px 12px !important;
  border-radius: 6px !important;
  font-weight: 500;
  transition: all 0.2s ease;
}
.el-button--text:hover { background: var(--dropdown-hover) !important; color: var(--text-link-hover) !important; }
.el-table .el-button--text { color: var(--text-link) !important; }
.el-table .el-button--text:hover { background: var(--dropdown-hover) !important; color: var(--text-link-hover) !important; }
.el-table__body tr:hover .el-button--text { color: var(--text-link-hover) !important; }

/* ----- 分页 ----- */
.el-pagination { padding: 10px 0; }
.el-pagination button, .el-pager li {
  background: var(--pager-bg) !important;
  color: var(--pager-color) !important;
  border-radius: 6px !important;
  transition: all 0.2s;
}
.el-pagination button:hover, .el-pager li:hover {
  color: var(--pager-hover-color) !important;
  background: var(--dropdown-hover) !important;
}
.el-pager li.active {
  background: linear-gradient(135deg, var(--pager-active-from), var(--pager-active-to)) !important;
  color: #fff !important;
}
.el-pagination .btn-next .el-icon, .el-pagination .btn-prev .el-icon { color: var(--pager-color) !important; }

/* ----- 单选按钮组 ----- */
.el-radio-button__inner {
  background: var(--radio-bg) !important;
  border-color: rgba(255,255,255,0.06) !important;
  color: var(--radio-color) !important;
  border-radius: 0 !important;
  transition: all 0.25s;
}
.el-radio-button__inner:hover { color: var(--pager-hover-color) !important; background: var(--dropdown-hover) !important; }
.el-radio-button:first-child .el-radio-button__inner { border-radius: 8px 0 0 8px !important; }
.el-radio-button:last-child .el-radio-button__inner { border-radius: 0 8px 8px 0 !important; }
.el-radio-button.is-active .el-radio-button__inner {
  background: var(--radio-active-bg) !important;
  border-color: var(--radio-active-border) !important;
  color: var(--brand-text) !important;
  box-shadow: none !important;
}

/* ----- Tabs ----- */
.el-tabs__item {
  color: var(--text-dim) !important;
  font-weight: 500;
  transition: all 0.25s;
}
.el-tabs__item:hover { color: var(--pager-hover-color) !important; }
.el-tabs__item.is-active { color: var(--tabs-active) !important; font-weight: 600; }
.el-tabs__nav-wrap::after { background-color: var(--table-td-border) !important; }
.el-tabs__active-bar { background: var(--tabs-bar) !important; height: 3px !important; border-radius: 2px; }

/* ----- 开关 ----- */
.el-switch__label { color: var(--text-dim) !important; }
.el-switch__label.is-active { color: var(--tag-primary-color) !important; }

/* ----- 下拉菜单 ----- */
.el-dropdown-menu {
  background: var(--dropdown-bg) !important;
  border: 1px solid var(--dropdown-border) !important;
  border-radius: 10px !important;
  box-shadow: 0 8px 32px rgba(0,0,0,0.4);
  padding: 4px 0;
}
.el-dropdown-menu__item {
  color: var(--table-td-color) !important;
  padding: 8px 18px;
  transition: all 0.2s;
  border-radius: 6px;
  margin: 2px 6px;
}
.el-dropdown-menu__item:hover {
  background: var(--dropdown-hover) !important;
  color: var(--dropdown-hover-text) !important;
}

/* ===== 页面标题 ===== */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 22px;
}
.page-header h3 {
  color: var(--page-title);
  margin: 0;
  font-size: 18px;
  font-weight: 700;
}

/* ===== 弹窗按钮 ===== */
.el-dialog__headerbtn .el-dialog__close { color: var(--text-dim) !important; font-size: 18px; }
.el-dialog__headerbtn .el-dialog__close:hover { color: var(--close-hover) !important; }

/* ===== MessageBox 确认框 ===== */
.el-message-box {
  background: var(--dropdown-bg) !important;
  border: 1px solid var(--dropdown-border) !important;
  border-radius: 14px !important;
}
.el-message-box__title { color: var(--dialog-title) !important; }
.el-message-box__message { color: var(--table-td-color) !important; }

/* ===== 滚动条 ===== */
::-webkit-scrollbar { width: 6px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: var(--scrollbar-thumb); border-radius: 3px; }
::-webkit-scrollbar-thumb:hover { background: var(--scrollbar-thumb-hover); }

/* ===== Popover & Dropdown - 使用固定深色确保可见 ===== */
/* 注：el-popper 被追加到 body 下，无法继承 #app 的 CSS 变量，所以用固定色值 */
.theme-popover.el-popper,
.theme-popover.el-popover {
  background: #1a1a2e !important;
  border: 1px solid rgba(244,114,182,0.15) !important;
  border-radius: 12px !important;
  color: #e5e7eb !important;
  box-shadow: 0 8px 32px rgba(0,0,0,0.5) !important;
}

.theme-popover .theme-card-name {
  color: #e5e7eb !important;
}

.theme-popover .theme-card-badge {
  color: #f0c4ff !important;
}

.theme-popover .theme-card.active .theme-card-name {
  color: #f0c4ff !important;
}

.theme-popover .theme-picker-header {
  border-bottom-color: rgba(244,114,182,0.1) !important;
}

/* Dropdown 菜单固定深色 */
.el-dropdown-menu.el-popper {
  background: #1a1a2e !important;
  border: 1px solid rgba(244,114,182,0.15) !important;
  border-radius: 10px !important;
  box-shadow: 0 8px 32px rgba(0,0,0,0.5) !important;
}

.el-dropdown-menu__item {
  color: #d1d5db !important;
}

.el-dropdown-menu__item:hover {
  background: rgba(244,114,182,0.1) !important;
  color: #f0c4ff !important;
}
</style>
